import React from "react";
import { ArrowLeft, Eye, Mail, FileText, Send, Clock } from "lucide-react";
import { useCase } from "../../context/CaseContext";
import { resolveCaseStatus } from "../../config/statusMapping";

interface EditorHeaderProps {
  onToggleHistory?: () => void;
}

export default function EditorHeader({ onToggleHistory }: EditorHeaderProps) {
  const {
    clientName,
    status,
    saveStatus,
    onBack,
    currentCaseId,
    totalCount,
    completedCount,
    setShowEmailModal,
    notionPageUrl,
    setShowHandoverModal,
    meeting,
    blockedItems,
    lastUpdated,
  } = useCase();

  // Pipeline Logic (Matches Dashboard.tsx)
  const resolved = resolveCaseStatus({
    status,
    totalCount,
    totalItems: totalCount,
    completedCount,
    blockedItems,
    lastUpdated,
    meeting,
  });

  const getStatusLabel = () => {
    switch (resolved.column) {
      case "awaiting_meeting":
        return {
          text: "AWAITING MEETING",
          color:
            "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
        };
      case "upcoming":
        return {
          text: "UPCOMING",
          color:
            "bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:border-teal-800",
        };
      case "in_progress":
        return {
          text: "IN PROGRESS",
          color:
            "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800",
        };
      case "stuck":
        return {
          text: "STUCK / BLOCKED",
          color:
            "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800",
        };
      case "review":
        return {
          text: "READY FOR REVIEW",
          color:
            "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800",
        };
      default:
        return {
          text: "UNKNOWN",
          color: "bg-gray-100 text-gray-700 border-gray-200",
        };
    }
  };

  const statusLabel = getStatusLabel();

  return (
    <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-md border-b border-gray-200/50 dark:border-slate-700/50 sticky top-0 z-50 transition-colors duration-200 supports-[backdrop-filter]:bg-white/60">
      <div className="max-w-5xl mx-auto px-4 h-16 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full text-slate-500"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex flex-col">
            <h1 className="text-lg font-bold text-slate-800 dark:text-white leading-tight">
              {clientName || "New Case"}
            </h1>
            <div className="flex items-center gap-2">
              <span
                className={`text-[10px] px-2 rounded-full font-bold uppercase border ${statusLabel.color}`}
              >
                {statusLabel.text}
              </span>
              {resolved.isStale && (
                <span className="px-1.5 py-0.5 rounded text-[8px] font-black bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 border border-orange-200 dark:border-orange-800 animate-pulse">
                  STALE
                </span>
              )}
              <span className="text-[10px] text-gray-400 font-mono">
                {saveStatus === "saved" ? "Saved" : "Saving..."}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Small Header Progress Bar */}
          <div className="hidden md:flex items-center gap-3 mr-2 pr-4 border-r border-gray-200 dark:border-slate-700 h-8">
            <span className="text-sm font-bold text-slate-700 dark:text-slate-200 tabular-nums">
              {totalCount === 0
                ? 0
                : Math.round((completedCount / totalCount) * 100)}
              %
            </span>
            <div className="w-24 bg-gray-100 dark:bg-slate-700 h-2.5 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all duration-500 ease-out"
                style={{
                  width: `${totalCount === 0 ? 0 : (completedCount / totalCount) * 100}%`,
                }}
              ></div>
            </div>
          </div>

          {/* History Button */}
          <button
            onClick={onToggleHistory}
            className="bg-white border border-gray-300 text-slate-700 hover:bg-gray-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200 px-3 py-2 rounded-lg text-sm font-bold flex items-center gap-2 shadow-sm transition-all"
            title="View History"
          >
            <Clock size={16} />{" "}
            <span className="hidden sm:inline">History</span>
          </button>

          <button
            onClick={() => setShowEmailModal(true)}
            className="bg-white border border-gray-300 text-slate-700 hover:bg-gray-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200 px-3 py-2 rounded-lg text-sm font-bold flex items-center gap-2 shadow-sm transition-all"
          >
            <Mail size={16} /> <span className="hidden sm:inline">Email</span>
          </button>

          {notionPageUrl ? (
            <a
              href={notionPageUrl}
              target="_blank"
              rel="noreferrer"
              className="bg-white border border-gray-300 text-slate-700 hover:bg-gray-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 shadow-sm transition-all"
            >
              <FileText size={16} />{" "}
              <span className="hidden sm:inline">Open Notion</span>
            </a>
          ) : (
            <button
              onClick={() => setShowHandoverModal(true)}
              className="bg-slate-900 dark:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 shadow-sm hover:bg-slate-800 transition-all"
            >
              <Send size={14} />{" "}
              <span className="hidden sm:inline">Handover</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
